import type { ItemDto } from 'cart/services/graphql.types'
import { ProductLocations } from 'product/types/ProductLocations'
import { useCallback } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { PageType } from 'types/analytics'
import { useCartDispatch, useCartState } from '../context/CartProvider'
import { useAnalytics } from './useAnalytics'
import { useNotifications } from './useNotifications'

interface NotificationHandlerProps {
	location: string
	notShowNotification?: boolean
	feedbackTestEnabled: boolean
	cartItems: ItemDto[]
	item: ItemDto
}

export const useNotificationHandler = () => {
	const { isSmall, isMedium } = useResponsive()
	const dispatch = useCartDispatch()
	const { showPreview, showShoppingCart } = useCartState()
	const { closeFeedbackEvent } = useAnalytics()

	const { addItemNotification, addItemTextNotification, addLargeNotification } =
		useNotifications()

	const openPreviewIfHidden = useCallback(() => {
		if (!showPreview && !showShoppingCart) {
			dispatch({ showPreview: 'auto' })
		}
	}, [dispatch, showPreview, showShoppingCart])

	const handleNotification = (props: NotificationHandlerProps): void => {
		const {
			location,
			feedbackTestEnabled,
			notShowNotification,
			item,
			cartItems,
		} = props

		const isShopTheLook =
			location === ProductLocations.ShopTheLook ||
			location === ProductLocations.ShopTheLookSimilars ||
			location === ProductLocations.ShopTheLookBestSellers ||
			location === ProductLocations.Edits

		const isMangoStylist = location === ProductLocations.MangoStylist
		const isInCart = location.includes(PageType.ShoppingCart)

		if (isInCart || isMangoStylist || isShopTheLook) {
			if (feedbackTestEnabled) {
				addItemTextNotification()
			} else {
				addItemNotification()
			}
			return
		}

		if (feedbackTestEnabled) {
			addLargeNotification({
				childrenProps: { item, cartItems, isSmall, closeFeedbackEvent },
			})
		} else {
			if ((isSmall || isMedium) && !notShowNotification) {
				addItemNotification()
			}
			openPreviewIfHidden()
		}
	}

	return { handleNotification }
}
